// MLS API Service
// Uses soomer-api MLS proxy for real estate listings

import { Listing, ListingImage, ListingFeatures } from '../types';

// Soomer API configuration
const SOOMER_API_URL = import.meta.env.VITE_SOOMER_API_URL || 'https://api.soomerlabs.com';
const APPLICATION_ID = import.meta.env.VITE_SOOMER_APPLICATION_ID || '';
const MLS_API_URL = `${SOOMER_API_URL}/mls/v1`;

// IDX API property structure (comprehensive)
interface IDXProperty {
  listingID: string;
  idxID?: string;
  address: string | null;
  cityName: string;
  state: string;
  zipcode: string;
  zip4?: string;
  listingPrice: string;
  price?: number;
  closePrice?: string;
  soldPrice?: string;
  salePrice?: string;
  bedrooms: number | string;
  totalBaths: number | string;
  fullBaths?: number | string;
  partialBaths?: number | string;
  sqFt: number | string;
  acres?: number | string;
  lotSqft?: number | string;
  lotSizeDimensions?: string;
  lotDim?: string;
  yearBuilt?: number | string;
  propType?: string;
  propSubType?: string;
  propStatus?: string;
  remarksConcat?: string;
  countyName?: string;
  image?: Record<string | number, { url: string; caption?: string } | number>;
  advanced?: Record<string, string[]>;
  dateAdded?: string;
  dateModified?: string;
  latitude?: number;
  longitude?: number;
  streetNumber?: string;
  streetName?: string;
  streetDirection?: string;
  unitNumber?: string;
  status?: string;
  soldDate?: string;
  idxStatus?: string;

  // Extended property details
  pricePerSqFt?: number | string;
  subdivision?: string;
  municipality?: string;
  mlsArea?: string;
  zoning?: string;

  // Construction/Structure
  architecturalStyle?: string[];
  constructionMaterials?: string[];
  foundationDetails?: string[];
  roof?: string[];
  wallCeilingTypes?: string[];
  levelsCount?: string[];
  propertyCondition?: string[];
  newConstructionYN?: string;

  // Interior
  roomList?: string[];
  storyList?: string[];
  flooringType?: string[];
  fireplaceYN?: string;
  fireplacesCount?: number | string;
  basementYN?: string;
  basementType?: string[];
  laundryType?: string[];
  appliances?: string[];

  // Exterior/Lot
  fencing?: string[];
  pool?: string[];
  outdoorLivingStructures?: string[];
  otherStructures?: string[];

  // Utilities/Systems
  heatingYN?: string;
  heatingType?: string[];
  heatingFuel?: string[];
  centralAirYN?: string;
  coolingType?: string[];
  coolingFuel?: string[];
  hotWater?: string[];
  waterSource?: string[];
  sewerSeptic?: string[];
  electricService?: string[];
  utilities?: string[];
  internetServices?: string[];

  // Parking
  garageYN?: string;
  typeOfParking?: string[];
  parkingFeatures?: string[];
  totalGarageAndParkingSpaces?: number | string;
  numOfAttachedCarportSpaces?: number | string;

  // HOA
  hoaYN?: string;
  hoaName?: string;
  hoaFee?: number | string;
  hoaFeeFreq?: string;
  hoaCondoCoopFeeIncludes?: string[];
  capitalContributionFee?: number | string;

  // Tax Info
  taxAnnualAmount?: number | string;
  taxAssessedValue?: number | string;
  taxYear?: number | string;
  taxLot?: string;

  // Schools
  schoolDistrictName?: string;
  highSchool?: string;
  middleOrJuniorSchool?: string;
  elementarySchool?: string;

  // Financial/Sale Info
  acceptableFinancing?: string[];
  inclusions?: string;
  exclusions?: string;
  possession?: string[];

  // Listing Agent
  listingAgentID?: string;
  listingAgentName?: string;
  listingAgentContacts?: string;
  listingEmail?: string;
  coListingAgentID?: string;
  coListingAgentName?: string;

  // Listing Office
  listingOfficeID?: string;
  listingOfficeName?: string;
  listingOfficeContacts?: string;
  listingOfficeEmail?: string;
  coListingOfficeName?: string;
  coListingOfficeEmail?: string;
}

// Safely convert a value (number or string) to a number
function toNumber(value: number | string | undefined | null, defaultValue: number = 0): number {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'number') return value;
  const cleaned = String(value).replace(/[^0-9.]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? defaultValue : parsed;
}

// Decode HTML entities in strings
function decodeHtmlEntities(text: string): string {
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#34;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&nbsp;': ' ',
    '&#x27;': "'",
    '&#x2F;': '/',
  };
  return text.replace(/&[#\w]+;/g, (entity) => entities[entity] || entity);
}

// Extract all images from the IDX image object
function extractImages(imageData: IDXProperty['image']): ListingImage[] {
  if (!imageData) return [];

  const images: ListingImage[] = [];
  for (const [key, value] of Object.entries(imageData)) {
    if (key === 'totalCount') continue;
    if (typeof value === 'object' && value?.url) {
      images.push({
        url: value.url,
        caption: value.caption || undefined
      });
    }
  }
  return images;
}

// Map IDX advanced features to our categorized format
function mapAdvancedFeatures(advanced: Record<string, string[]> | undefined): ListingFeatures {
  if (!advanced) return {};

  const featureMapping: Record<string, keyof ListingFeatures> = {
    'Rooms': 'rooms',
    'Parking': 'parking',
    'Exterior': 'exterior',
    'Interior': 'interior',
    'Flooring': 'flooring',
    'HeatCool': 'heating',
    'Community': 'community',
    'Construction': 'construction',
    'Utilities': 'utilities',
    'Inclusions': 'appliances',
    'Recreation': 'recreation',
    'Lot': 'lot',
    'LotSize': 'lot',
    'Roof': 'roof',
    'Style': 'style',
    'Condo': 'style',
    'Stories': 'style',
    'Scenery': 'exterior',
    'Fencing': 'exterior',
    'WinDoors': 'interior',
    'Laundry': 'appliances',
    'Fireplaces': 'interior',
    'Energy': 'utilities',
    'General': 'community',
    'Location': 'lot',
    'Condition': 'interior',
    'Financial': 'community'
  };

  const features: ListingFeatures = {};

  for (const [category, items] of Object.entries(advanced)) {
    const mappedKey = featureMapping[category];
    if (mappedKey && items?.length) {
      if (!features[mappedKey]) {
        features[mappedKey] = [];
      }
      // Decode HTML entities in feature strings
      const decodedItems = items.map(item => decodeHtmlEntities(item));
      features[mappedKey] = [...(features[mappedKey] || []), ...decodedItems];
    }
  }

  return features;
}

// Helper to convert "yes"/"no" strings to boolean
function toBoolean(value: string | undefined): boolean | undefined {
  if (!value) return undefined;
  return value.toLowerCase() === 'yes';
}

// Transform IDX Broker property to our Listing format
function transformProperty(property: IDXProperty, defaultStatus: 'sold' | 'active' | 'pending' = 'sold'): Listing {
  const images = extractImages(property.image);
  const primaryImage = images[0]?.url || '';

  // Derive status from propStatus if available (case-insensitive)
  let status: 'sold' | 'active' | 'pending' = defaultStatus;
  if (property.propStatus) {
    const propStatusLower = property.propStatus.toLowerCase();
    if (propStatusLower === 'sold' || propStatusLower === 'closed') {
      status = 'sold';
    } else if (propStatusLower === 'pending' || propStatusLower === 'under contract' || propStatusLower === 'contingent') {
      status = 'pending';
    } else if (propStatusLower === 'active' || propStatusLower === 'for sale') {
      status = 'active';
    }
  }

  // For sold listings, prefer sold/close price over list price
  const soldPriceStr = property.closePrice || property.soldPrice || property.salePrice;
  const priceStr = (status === 'sold' && soldPriceStr) ? soldPriceStr : (property.listingPrice || '0');
  const price = toNumber(priceStr);

  // Calculate days on market if dateAdded exists
  let daysOnMarket: number | undefined;
  if (property.dateAdded && property.dateAdded !== '-0001-11-30T00:00:00+00:00') {
    const addedDate = new Date(property.dateAdded);
    const now = new Date();
    daysOnMarket = Math.floor((now.getTime() - addedDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysOnMarket < 0) daysOnMarket = undefined;
  }

  return {
    id: property.listingID,
    idxID: property.idxID || undefined,
    address: property.address || 'Address Not Available',
    city: property.cityName || '',
    state: property.state || 'PA',
    zip: property.zipcode || '',
    price: Math.round(price),
    beds: Math.round(toNumber(property.bedrooms)),
    baths: toNumber(property.totalBaths),
    fullBaths: property.fullBaths ? Math.round(toNumber(property.fullBaths)) : undefined,
    partialBaths: property.partialBaths ? Math.round(toNumber(property.partialBaths)) : undefined,
    sqft: Math.round(toNumber(property.sqFt)),
    image: primaryImage,
    images: images,
    status: status,
    propStatus: property.propStatus || undefined,
    soldDate: property.soldDate,

    // Extended details
    acres: property.acres ? toNumber(property.acres) : undefined,
    lotSqFt: property.lotSqft ? Math.round(toNumber(property.lotSqft)) : undefined,
    lotDimensions: property.lotSizeDimensions || property.lotDim || undefined,
    yearBuilt: property.yearBuilt ? Math.round(toNumber(property.yearBuilt)) : undefined,
    propType: property.propType || undefined,
    propSubType: property.propSubType || undefined,
    description: property.remarksConcat ? decodeHtmlEntities(property.remarksConcat) : undefined,
    county: property.countyName || undefined,
    subdivision: property.subdivision || undefined,
    municipality: property.municipality || undefined,
    mlsArea: property.mlsArea || undefined,
    zoning: property.zoning || undefined,
    pricePerSqFt: property.pricePerSqFt ? toNumber(property.pricePerSqFt) : undefined,

    // Construction/Structure
    architecturalStyle: property.architecturalStyle || undefined,
    constructionMaterials: property.constructionMaterials || undefined,
    foundationDetails: property.foundationDetails || undefined,
    roof: property.roof || undefined,
    wallCeilingTypes: property.wallCeilingTypes || undefined,
    levelsCount: property.levelsCount || undefined,
    propertyCondition: property.propertyCondition || undefined,
    newConstruction: toBoolean(property.newConstructionYN),

    // Interior
    roomList: property.roomList || undefined,
    storyList: property.storyList || undefined,
    flooringType: property.flooringType || undefined,
    fireplaces: property.fireplacesCount ? Math.round(toNumber(property.fireplacesCount)) : undefined,
    hasFireplace: toBoolean(property.fireplaceYN),
    hasBasement: toBoolean(property.basementYN),
    basementType: property.basementType || undefined,
    laundryType: property.laundryType || undefined,
    appliances: property.appliances || undefined,

    // Exterior/Lot
    fencing: property.fencing || undefined,
    pool: property.pool || undefined,
    outdoorLivingStructures: property.outdoorLivingStructures || undefined,
    otherStructures: property.otherStructures || undefined,

    // Utilities/Systems
    hasHeating: toBoolean(property.heatingYN),
    heatingType: property.heatingType || undefined,
    heatingFuel: property.heatingFuel || undefined,
    hasCentralAir: toBoolean(property.centralAirYN),
    coolingType: property.coolingType || undefined,
    coolingFuel: property.coolingFuel || undefined,
    hotWater: property.hotWater || undefined,
    waterSource: property.waterSource || undefined,
    sewerSeptic: property.sewerSeptic || undefined,
    electricService: property.electricService || undefined,
    utilities: property.utilities || undefined,
    internetServices: property.internetServices || undefined,

    // Parking
    hasGarage: toBoolean(property.garageYN),
    typeOfParking: property.typeOfParking || undefined,
    parkingFeatures: property.parkingFeatures || undefined,
    totalParkingSpaces: property.totalGarageAndParkingSpaces ? Math.round(toNumber(property.totalGarageAndParkingSpaces)) : undefined,
    carportSpaces: property.numOfAttachedCarportSpaces ? Math.round(toNumber(property.numOfAttachedCarportSpaces)) : undefined,

    // HOA
    hasHOA: toBoolean(property.hoaYN),
    hoaName: property.hoaName || undefined,
    hoaFee: property.hoaFee ? toNumber(property.hoaFee) : undefined,
    hoaFeeFrequency: property.hoaFeeFreq || undefined,
    hoaIncludes: property.hoaCondoCoopFeeIncludes || undefined,
    capitalContributionFee: property.capitalContributionFee ? toNumber(property.capitalContributionFee) : undefined,

    // Tax Info
    taxAnnualAmount: property.taxAnnualAmount ? toNumber(property.taxAnnualAmount) : undefined,
    taxAssessedValue: property.taxAssessedValue ? toNumber(property.taxAssessedValue) : undefined,
    taxYear: property.taxYear ? Math.round(toNumber(property.taxYear)) : undefined,
    taxLot: property.taxLot || undefined,

    // Schools
    schoolDistrict: property.schoolDistrictName || undefined,
    highSchool: property.highSchool || undefined,
    middleSchool: property.middleOrJuniorSchool || undefined,
    elementarySchool: property.elementarySchool || undefined,

    // Financial/Sale Info
    acceptableFinancing: property.acceptableFinancing || undefined,
    inclusions: property.inclusions || undefined,
    exclusions: property.exclusions || undefined,
    possession: property.possession || undefined,

    // Features (from advanced field)
    features: mapAdvancedFeatures(property.advanced),
    advancedFeatures: property.advanced,

    // Listing Agent
    listingAgent: {
      id: property.listingAgentID || undefined,
      name: property.listingAgentName || undefined,
      phone: property.listingAgentContacts || undefined,
      email: property.listingEmail || undefined,
    },
    coListingAgent: property.coListingAgentName ? {
      id: property.coListingAgentID || undefined,
      name: property.coListingAgentName || undefined,
    } : undefined,
    listingAgentId: property.listingAgentID || undefined,
    listingAgentName: property.listingAgentName || undefined,
    listingAgentPhone: property.listingAgentContacts || undefined,
    listingAgentEmail: property.listingEmail || undefined,

    // Listing Office
    listingOffice: {
      id: property.listingOfficeID || undefined,
      name: property.listingOfficeName || undefined,
      phone: property.listingOfficeContacts || undefined,
      email: property.listingOfficeEmail || undefined,
    },
    coListingOffice: property.coListingOfficeName ? {
      name: property.coListingOfficeName || undefined,
      email: property.coListingOfficeEmail || undefined,
    } : undefined,
    listingOfficeId: property.listingOfficeID || undefined,
    listingOfficeName: property.listingOfficeName || undefined,

    // MLS Info
    mlsNumber: property.listingID,
    daysOnMarket,
    dateAdded: property.dateAdded !== '-0001-11-30T00:00:00+00:00' ? property.dateAdded : undefined,

    // Location
    latitude: property.latitude || undefined,
    longitude: property.longitude || undefined,
    streetNumber: property.streetNumber || undefined,
    streetName: property.streetName || undefined,
    streetDirection: property.streetDirection || undefined,
    unitNumber: property.unitNumber || undefined
  };
}

// Parse IDX response - handles both direct API and proxy responses
function parseIDXResponse(data: any): any[] {
  if (!data) return [];

  // If response has a 'data' property with the listings
  if (data.data && typeof data.data === 'object') {
    return Object.values(data.data);
  }

  // If response is already an object of listings
  if (typeof data === 'object' && !Array.isArray(data)) {
    const metaKeys = ['total', 'first', 'last', 'next', 'previous', 'data'];
    return Object.entries(data)
      .filter(([key]) => !metaKeys.includes(key))
      .map(([, value]) => value);
  }

  return Array.isArray(data) ? data : [];
}

// Make MLS API request via soomer-api proxy
async function fetchMLS(endpoint: string): Promise<any> {
  const response = await fetch(`${MLS_API_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'X-Soomer-Application-ID': APPLICATION_ID,
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    console.error('MLS API error:', response.status, errorText);
    throw new Error(`MLS API request failed: ${response.status}`);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

// Fetch sold/pending listings
export async function fetchSoldListings(): Promise<Listing[]> {
  try {
    const data = await fetchMLS('/clients/soldpending');
    if (!data) return [];

    const listings = parseIDXResponse(data);
    return listings
      .map((prop: any) => {
        try { return transformProperty(prop, 'sold'); }
        catch { return null; }
      })
      .filter((listing): listing is Listing => listing !== null);
  } catch (error) {
    console.error('Error fetching sold listings:', error);
    return [];
  }
}

// Fetch featured/active listings
export async function fetchActiveListings(): Promise<Listing[]> {
  try {
    const data = await fetchMLS('/clients/featured');
    if (!data) return [];

    const rawListings = data.data && typeof data.data === 'object'
      ? Object.values(data.data)
      : parseIDXResponse(data);

    return (rawListings as any[])
      .map((prop) => {
        try { return transformProperty(prop, 'active'); }
        catch { return null; }
      })
      .filter((listing): listing is Listing => listing !== null);
  } catch (error) {
    console.error('Error fetching active listings:', error);
    return [];
  }
}

// Fetch a single listing directly by idxID and listingID
export async function fetchListingDirect(idxID: string, listingID: string): Promise<Listing | null> {
  try {
    const data = await fetchMLS(`/clients/listing/${idxID}/${listingID}`);
    if (!data) return null;

    // The API might return the listing directly or wrapped in data
    const property = data.data || data;
    if (!property || !property.listingID) return null;

    // Add idxID to the property since we know it
    property.idxID = idxID;

    // Debug: Log agent info from API response
    console.log('API Response - Agent Info:', {
      listingAgentName: property.listingAgentName,
      listingAgentContacts: property.listingAgentContacts,
      listingEmail: property.listingEmail,
      listingOfficeName: property.listingOfficeName,
    });

    const listing = transformProperty(property, property.idxStatus === 'sold' ? 'sold' : 'active');

    // Debug: Log transformed listing agent info
    console.log('Transformed Listing - Agent Info:', {
      listingAgent: listing.listingAgent,
      listingAgentName: listing.listingAgentName,
      listingAgentPhone: listing.listingAgentPhone,
      listingAgentEmail: listing.listingAgentEmail,
    });

    return listing;
  } catch (error) {
    console.error('Error fetching listing directly:', error);
    return null;
  }
}

// Fetch a single listing by ID (searches both active and sold)
export async function fetchListingById(id: string, idxID?: string): Promise<Listing | null> {
  try {
    // If we have both idxID and listingID, try direct API first
    if (idxID) {
      const directListing = await fetchListingDirect(idxID, id);
      if (directListing) return directListing;
    }

    // Fallback: Try active listings first
    const activeListings = await fetchActiveListings();
    const activeListing = activeListings.find(l => l.id === id);
    if (activeListing) return activeListing;

    // Try sold listings
    const soldListings = await fetchSoldListings();
    const soldListing = soldListings.find(l => l.id === id);
    if (soldListing) return soldListing;

    return null;
  } catch {
    return null;
  }
}

// Fetch agent details by ID
export async function fetchAgentById(agentId: string): Promise<{ name: string; email?: string; phone?: string } | null> {
  try {
    const data = await fetchMLS(`/mls/listingagents/${agentId}`);
    if (!data) return null;

    const agentData = data.data || data;
    return {
      name: agentData.agentDisplayName || agentData.agentName ||
            `${agentData.agentFirstName || ''} ${agentData.agentLastName || ''}`.trim() || null,
      email: agentData.agentEmail || undefined,
      phone: agentData.agentPhone || agentData.agentCellPhone || undefined
    };
  } catch {
    return null;
  }
}

// Fetch office details by ID
export async function fetchOfficeById(officeId: string): Promise<{ name: string; phone?: string } | null> {
  try {
    const data = await fetchMLS(`/mls/listingoffices/${officeId}`);
    if (!data) return null;

    const officeData = data.data || data;
    return {
      name: officeData.officeName || officeData.officeDisplayName || null,
      phone: officeData.officePhone || undefined
    };
  } catch {
    return null;
  }
}
